import { add, set } from "date-fns";
import {
  AvailableInterval,
  GroupBookingInterval,
  TimeSlot,
} from "../AppDataProvider/types";
import {
  bookingWithPrice,
  defaultBooking,
  defaultRecurringBooking,
} from "./bookings";
import { defaultBookingTool, extendedServiceBookingTool } from "./bookingTool";
import {
  defaultService,
  extendedSingleDurationPaidService,
  multiDurationService,
} from "./service";
import {
  GetOrganisationAvailabilityIntervalsDocument,
  GetOrganisationAvailabilityIntervalsQuery,
  GetOrganisationAvailabilityIntervalsQueryVariables,
} from "../GeneratedGraphQL";

import { defaultOrganisation } from "./organisation";
import { defaultResource } from "./resource";
import { defaultResourceGroup } from "./resourceGroup";
import { MockedResponse } from "@apollo/client/testing";

/**
 * Mock intervals for the 1st day of the month
 */

const firstDayRanges = [
  {
    end: new Date("2124-01-02T00:00:00").toISOString(),
    start: new Date("2124-01-01T00:00:00").toISOString(),
  },
];

export const defaultAvailabilityIntervalsRequestVariables: GetOrganisationAvailabilityIntervalsQueryVariables =
  {
    bookingToolId: defaultBookingTool._id,
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: firstDayRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

/**
 * An extended interval request that would occur in the case of a long
 * service duration. In this case two days.
 */
const extendedDayRanges = [
  {
    end: new Date("2124-01-03T00:00:00").toISOString(),
    start: new Date("2124-01-01T00:00:00").toISOString(),
  },
];

export const extendedAvailabilityIntervalsRequestVariables: GetOrganisationAvailabilityIntervalsQueryVariables =
  {
    bookingToolId: extendedServiceBookingTool._id,
    duration: 2880,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: extendedDayRanges,
    serviceId: extendedSingleDurationPaidService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

export const nextMonthAvailabilityIntervalsRequestVariables: GetOrganisationAvailabilityIntervalsQueryVariables =
  {
    bookingToolId: defaultBookingTool._id,
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: [
      {
        end: new Date("2124-02-07T00:00:00").toISOString(),
        start: new Date("2124-02-06T00:00:00").toISOString(),
      },
    ],
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

export const nextMonthAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "AvailableInterval",

    end: "2124-02-06T17:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-02-06T17:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-02-06T19:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-02-06T18:30:00.000Z",
  },
];

export const genericAvailabilityIntervalsRequestVariables: GetOrganisationAvailabilityIntervalsQueryVariables =
  {
    duration: 30,
    id: defaultOrganisation._id,
    meta: {
      sessionId: "12345678",
    },

    ranges: firstDayRanges,
    serviceId: defaultService._id,
    spaces: 1,
    timezone: "Europe/London",
  };

export const defaultAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "AvailableInterval",

    end: "2124-01-01T09:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T09:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T10:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T09:30:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T10:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T10:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T11:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T10:30:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T11:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T11:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T12:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T11:30:00.000Z",
  },
];

export const largeAmountOfAvailabilityIntervals = (): AvailableInterval[] => {
  const intervals: AvailableInterval[] = [
    {
      __typename: "AvailableInterval",

      end: `2124-01-01T00:00:00.000Z`,
      resourceIds: [defaultResource._id],
      start: `2124-01-01T00:00:00.000Z`,
    },
  ];
  for (let i = 1; i < 60; i++) {
    intervals.push({
      __typename: "AvailableInterval",

      end: `2124-01-01T00:${i < 9 ? `0${i + 1}` : i + 1}:00.000Z`,
      resourceIds: [defaultResource._id],
      start: `2124-01-01T00:${i < 10 ? `0${i}` : i}:00.000Z`,
    });
  }
  return intervals;
};

export const extendedAvailabilityIntervals: AvailableInterval[] = [
  {
    __typename: "AvailableInterval",
    end: "2124-01-03T00:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T00:00:00.000Z",
  },
];

export const otherAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T16:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T17:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T17:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T17:30:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T18:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T19:00:00.000Z",
  },
];

export const recurringAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "GroupBookingInterval",
    booking: defaultRecurringBooking,
    end: "2124-01-01T10:00:00.000Z",
    remainingSpaces: 13,
    start: "2124-01-01T09:30:00.000Z",
  },
  {
    __typename: "GroupBookingInterval",
    booking: defaultRecurringBooking,
    end: "2124-01-01T10:30:00.000Z",
    remainingSpaces: 6,
    start: "2124-01-01T10:00:00.000Z",
  },
];

export const defaultAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: defaultAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const extendedAvailablilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: extendedAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: extendedAvailabilityIntervals,
        },
      },
    },
  };
export const nextMonthAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: nextMonthAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: nextMonthAvailabilityIntervals,
        },
      },
    },
  };

export const twoSpacesAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        spaces: 2,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: otherAvailabilityIntervals,
        },
      },
    },
  };

export const multiDurationAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        duration: 30,
        serviceId: multiDurationService._id,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const otherDurationAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        duration: 60,
        serviceId: multiDurationService._id,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: otherAvailabilityIntervals,
        },
      },
    },
  };

export const genericAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: genericAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const recurringAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: defaultAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: recurringAvailabilityIntervals,
        },
      },
    },
  };

export const resourceAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        meta: {
          sessionId: "24681012",
        },
        resourceIds: [defaultResource._id],
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const resourceGroupAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        meta: {
          sessionId: "135790123",
        },
        resourceGroupIds: [defaultResourceGroup._id],
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const multiServiceAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...defaultAvailabilityIntervalsRequestVariables,
        id: "multiple pages of services",
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const noAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: defaultAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: [],
        },
      },
    },
  };

const chainedRanges = [...new Array(9).keys()].map((count) => {
  const baseDate = add(new Date("2124-01-01T00:00:00"), {
    months: count,
  });

  const rangeStart = set(baseDate, {
    date: 1,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const rangeEnd = set(
    add(baseDate, {
      months: 1,
    }),
    {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },
  );

  return {
    end: rangeEnd.toISOString(),
    start: rangeStart.toISOString(),
  };
});

const chainedRangeAvailabilityIntervalsRequestVariables = {
  ...defaultAvailabilityIntervalsRequestVariables,
  ranges: chainedRanges,
  requestRangesIndividually: true,
};

export const chainedRangeAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: chainedRangeAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: defaultAvailabilityIntervals,
        },
      },
    },
  };

export const largeAmountOfAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: chainedRangeAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: largeAmountOfAvailabilityIntervals(),
        },
      },
    },
  };

export const groupBookingAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "GroupBookingInterval",
    booking: { ...defaultBooking, remainingSpaces: 4 },
    end: "2022-11-12T09:00:00.000Z",
    remainingSpaces: 4,
    start: "2022-11-12T09:00:00.000Z",
  },
  {
    __typename: "GroupBookingInterval",
    booking: {
      ...defaultBooking,
      price: 10,
      remainingSpaces: 3,
    },
    end: "2022-11-13T09:30:00.000Z",
    remainingSpaces: 3,
    start: "2022-11-13T09:00:00.000Z",
  },
  {
    __typename: "GroupBookingInterval",
    booking: bookingWithPrice,
    end: "2124-01-01T09:30:00.000Z",
    remainingSpaces: 1,
    start: "2124-01-01T09:00:00.000Z",
  },
];

export const chainedRangeGroupBookingAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: {
        ...chainedRangeAvailabilityIntervalsRequestVariables,
        duration: 30,
      },
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: groupBookingAvailabilityIntervals,
        },
      },
    },
  };

export const chainedRangeAvailabilityIntervalsErrorRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    error: new Error("Oops, some other time maybe."),
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: chainedRangeAvailabilityIntervalsRequestVariables,
    },
  };

export const chainedRecurringAvailabilityIntervalsRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: chainedRangeAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: recurringAvailabilityIntervals,
        },
      },
    },
  };

export const noAvailabilityIntervalsChainedRequest: MockedResponse<GetOrganisationAvailabilityIntervalsQuery> =
  {
    request: {
      query: GetOrganisationAvailabilityIntervalsDocument,
      variables: chainedRangeAvailabilityIntervalsRequestVariables,
    },
    result: {
      data: {
        organisation: {
          _id: defaultOrganisation._id,
          availabilityIntervals: [],
        },
      },
    },
  };

export const defaultTimeSlots: TimeSlot[] = [
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T09:30:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T09:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T10:00:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T09:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T10:30:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T10:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T11:00:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T10:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T11:30:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T11:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T12:00:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T11:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T12:30:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T12:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T13:00:00").toISOString(),
      resourceIds: [],
      start: new Date("2124-01-01T12:30:00").toISOString(),
    },
  },
];

export const recurringBookingTimeSlots: TimeSlot[] = [
  {
    interval: {
      __typename: "GroupBookingInterval",
      booking: defaultRecurringBooking,
      end: defaultRecurringBooking.end,
      remainingSpaces: 1,
      start: defaultRecurringBooking.start,
    },
  },
];
